<template>
  <div class="user-management-container">
    <h2>User Management</h2>

    <button @click="openAddUserModal" class="btn btn-add">Add New User</button>

    <table class="user-table" v-if="users.length > 0 && !loading">
      <thead>
        <tr>
          <th>ID</th>
          <th>Username</th>
          <th>Role</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="user in users" :key="user.ID">
          <td>{{ user.ID }}</td>
          <td>{{ user.Username }}</td>
          <td>{{ user.Role }}</td>
          <td>
            <button @click="editUser(user)" class="btn btn-edit">Edit</button>
            <button @click="confirmDelete(user.ID)" class="btn btn-delete">Delete</button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else-if="!loading && users.length === 0">No users found.</p>
    <div v-if="showEditModal" class="modal-overlay">
      <div class="modal-content">
        <h3>{{ editingUser.ID ? 'Edit User' : 'Add New User' }}</h3>
        <form @submit.prevent="saveUser">
          <div class="form-group">
            <label for="editUsername">Username:</label>
            <input type="text" id="editUsername" v-model="editingUser.Username" required />
          </div>
          <div class="form-group">
            <label for="editRole">Role:</label>
            <select id="editRole" v-model="editingUser.Role" required>
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div class="form-group">
            <label for="editPassword">
              {{ editingUser.ID ? 'New Password (leave blank to keep current):' : 'Password:' }}
            </label>
            <input type="password" id="editPassword" v-model="editingUser.Password"
                   :required="!editingUser.ID" />
          </div>
          <div class="modal-actions">
            <button type="submit" class="btn btn-primary">Save</button>
            <button type="button" @click="cancelEdit" class="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
// Base URL for your API
const API_BASE_URL = 'https://api.pala.linksoftindo.com';
import Swal from 'sweetalert2'; // <-- Impor SweetAlert2 di sini

export default {
  name: 'UserManagementPage',
  data() {
    return {
      users: [],
      loading: false,
      error: null,
      showEditModal: false,
      editingUser: {
        ID: null,
        username: '', // Lowercase property
        role: 'owner', // Lowercase property, default for new users
        password: '', // Lowercase property
      },
    };
  },
  computed: {
    isAdmin() {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        // Check for lowercase 'role' property
        return user && (user.role === 'admin' || user.role === 'owner');
      } catch (e) {
        console.error("Local Storage Error: Could not parse 'user' data.", e);
        return false;
      }
    },
    getToken() {
      return localStorage.getItem('token');
    }
  },
  async created() {
    if (!this.isAdmin) {
      // Ganti alert() dengan SweetAlert2
      await Swal.fire({
        icon: 'error',
        title: 'Akses Ditolak!',
        text: 'Anda tidak memiliki hak akses untuk halaman ini. Silakan login sebagai administrator.',
        confirmButtonText: 'OK'
      });
      this.$router.push('/');
      return;
    }
    await this.fetchUsers();
  },
  methods: {
    /**
     * Generic API call wrapper to handle fetching, headers, and parsing responses.
     * This centralizes fetch logic, prevents "body stream already read" errors,
     * and provides consistent error handling.
     * @param {string} url - The API endpoint URL (relative to API_BASE_URL).
     * @param {object} options - Fetch options (method, headers, body, etc.).
     * @returns {Promise<object|string|null>} - Parsed JSON object, raw text, or null if 204 No Content.
     * @throws {Error} - If the response is not ok (HTTP status 4xx/5xx).
     */
    async apiCall(url, options = {}) {
      this.error = null; // Clear any previous error

      try {
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getToken}`,
          ...options.headers, // Allow overriding or adding custom headers
        };

        const response = await fetch(`${API_BASE_URL}${url}`, { ...options, headers });
        let responseData = null;

        if (response.status === 204) {
          console.log(`API Call: ${url} returned 204 No Content.`);
          return null;
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            responseData = await response.json();
          } catch (jsonParseErr) {
            const rawText = await response.text();
            console.error(`API Call JSON Parse Error for ${url}:`, jsonParseErr, 'Raw response:', rawText);
            throw new Error(`Failed to parse server response as JSON. Raw: ${rawText.substring(0, 100)}...`);
          }
        } else {
          responseData = await response.text();
          console.warn(`API Call: ${url} returned non-JSON content-type: ${contentType || 'none'}. Parsed as text.`);
        }

        if (!response.ok) {
          const serverErrorMessage = (typeof responseData === 'object' && responseData && responseData.error)
                                      ? responseData.error
                                      : (typeof responseData === 'string' && responseData.length > 0)
                                        ? responseData
                                        : 'An unknown server error occurred.';
          throw new Error(`Error (HTTP ${response.status}): ${serverErrorMessage}`);
        }

        return responseData;
      } catch (err) {
        console.error(`API Call Failed (${options.method || 'GET'}) ${url}:`, err);
        this.error = err.message || 'An unexpected error occurred during API call.';
        // Tampilkan error menggunakan SweetAlert2
        Swal.fire({
          icon: 'error',
          title: 'Terjadi Kesalahan!',
          text: this.error,
          confirmButtonText: 'OK'
        });
        throw err;
      }
    },

    // --- Fetch All Users ---
    async fetchUsers() {
      this.loading = true;
      try {
        const data = await this.apiCall('/api/users/');
        this.users = Array.isArray(data) ? data : [];
        console.log("Users loaded successfully:", this.users.length, "users.");
        console.log(this.users);
      } catch (err) {
        // Error sudah ditangani di apiCall, tidak perlu alert lagi di sini
        // alert(`Failed to load users: ${this.error}`);
      } finally {
        this.loading = false;
      }
    },

    // --- Modal Control ---
    openAddUserModal() {
      this.editingUser = {
        ID: null,
        Username: '', // Pastikan properti tetap 'Username' sesuai API Anda
        Role: 'owner', // Default role for new users
        Password: '', // Tetap 'Password'
      };
      this.showEditModal = true;
    },
    editUser(user) {
      // Pastikan properti sesuai dengan properti objek user dari API (misal: Username, Role, bukan lowercase)
      this.editingUser = {
        ID: user.ID,
        Username: user.Username,
        Role: user.Role,
        Password: '', // Always clear password when opening for edit
      };
      console.log(this.editingUser);
      this.showEditModal = true;
    },
    cancelEdit() {
      this.showEditModal = false;
      this.editingUser = { ID: null, Username: '', Role: 'owner', Password: '' }; // Reset form
    },

    // --- Save User (Create or Update) ---
    async saveUser() {
      if (this.editingUser.ID) {
        await this.updateUser();
      } else {
        await this.createUser();
      }
    },

    async createUser() {
      this.loading = true;
      try {
        const payload = {
          username: this.editingUser.Username,
          role: this.editingUser.Role,
          password: this.editingUser.Password,
        };
        console.log(payload);
        await this.apiCall('/api/auth/register', {
          method: 'POST',
          body: JSON.stringify(payload),
        });

        await this.fetchUsers();
        this.showEditModal = false;
        // Ganti alert() dengan SweetAlert2
        Swal.fire({
          icon: 'success',
          title: 'Berhasil!',
          text: 'User berhasil ditambahkan!',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (err) {
        // Error sudah ditangani di apiCall
        // alert(`Failed to create user: ${this.error}`);
      } finally {
        this.loading = false;
      }
    },

    async updateUser() {
      this.loading = true;
      try {
        const payload = {
          username: this.editingUser.Username,
          role: this.editingUser.Role,
        };
        if (this.editingUser.Password) {
          payload.password = this.editingUser.Password; // Kirim password baru jika diisi
        }

        await this.apiCall(`/api/users/${this.editingUser.ID}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });

        await this.fetchUsers();
        this.showEditModal = false;
        // Ganti alert() dengan SweetAlert2
        Swal.fire({
          icon: 'success',
          title: 'Berhasil!',
          text: 'User berhasil diperbarui!',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (err) {
        // Error sudah ditangani di apiCall
        // alert(`Failed to update user: ${this.error}`);
      } finally {
        this.loading = false;
      }
    },

    async confirmDelete(userId) {
      // Ganti confirm() dengan SweetAlert2
      const result = await Swal.fire({
        title: 'Anda Yakin?',
        text: "Anda tidak akan dapat mengembalikan ini!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya, Hapus!',
        cancelButtonText: 'Batal'
      });

      if (result.isConfirmed) {
        this.loading = true;
        try {
          await this.apiCall(`/api/users/${userId}`, { method: 'DELETE' });

          this.users = this.users.filter(user => user.ID !== userId);
          // Ganti alert() dengan SweetAlert2
          Swal.fire(
            'Dihapus!',
            'User berhasil dihapus.',
            'success'
          );
        } catch (err) {
          // Error sudah ditangani di apiCall
          // alert(`Failed to delete user: ${this.error}`);
        } finally {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
/* Add styling for the new "Add New User" button */
.btn-add {
  background-color: #007bff; /* Blue color */
  color: white;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  margin-bottom: 20px; /* Space it from the table */
  font-size: 1em;
}

.btn-add:hover {
  background-color: #0056b3;
}

/* Your existing CSS styles go here. (No changes needed for existing styles) */
.user-management-container {
  max-width: 900px;
  margin: 40px auto;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

h2 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
}

.loading, .error-message {
  text-align: center;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 5px;
}

.loading {
  background-color: #e0f7fa;
  color: #007bb5;
}

.error-message {
  background-color: #ffebee;
  color: #d32f2f;
}

.user-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.user-table th, .user-table td {
  border: 1px solid #ddd;
  padding: 12px;
  text-align: left;
}

.user-table th {
  background-color: #4CAF50;
  color: white;
  text-transform: uppercase;
}

.user-table tr:nth-child(even) {
  background-color: #f2f2f2;
}

.user-table tr:hover {
  background-color: #ddd;
}

.btn {
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  margin-right: 5px;
}

.btn-edit {
  background-color: #2196F3;
  color: white;
}

.btn-edit:hover {
  background-color: #0b7dda;
}

.btn-delete {
  background-color: #f44336;
  color: white;
}

.btn-delete:hover {
  background-color: #da190b;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  width: 90%;
  max-width: 500px;
}

.modal-content h3 {
  text-align: center;
  margin-bottom: 25px;
  color: #333;
}

.form-group {
  margin-bottom: 15px;
  text-align: left;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #555;
}

.form-group input[type="text"],
.form-group input[type="password"],
.form-group select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1em;
  box-sizing: border-box;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 25px;
}

.btn-primary {
  background-color: #4CAF50;
  color: white;
  padding: 10px 20px;
}
.btn-primary:hover {
  background-color: #45a049;
}

.btn-secondary {
  background-color: #f0f0f0;
  color: #333;
  padding: 10px 20px;
  border: 1px solid #ccc;
}
.btn-secondary:hover {
  background-color: #e0e0e0;
}
</style>